import React, { useRef, useEffect } from 'react';
import SignaturePadLib from 'signature_pad';

export const SIGNATURE_STYLES = [
  { id: 'classic', label: 'Classic', font: 'italic 36px "Georgia", serif', color: '#1a1a2e' },
  { id: 'elegant', label: 'Elegant', font: 'italic 38px "Brush Script MT", "Segoe Script", cursive', color: '#1a1a2e' },
  { id: 'formal', label: 'Formal', font: '36px "Times New Roman", Times, serif', color: '#000000' },
  { id: 'bold', label: 'Bold', font: 'bold italic 34px "Palatino Linotype", "Book Antiqua", serif', color: '#0d0d2b' },
  { id: 'modern', label: 'Modern', font: 'italic 32px "Lucida Handwriting", "Comic Sans MS", cursive', color: '#2c3e50' },
];

export const generateTypedSignature = (name, styleId = 'classic') => {
  const style = SIGNATURE_STYLES.find(s => s.id === styleId) || SIGNATURE_STYLES[0];
  const sigCanvas = document.createElement('canvas');
  sigCanvas.width = 400;
  sigCanvas.height = 100;
  const ctx = sigCanvas.getContext('2d');
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, 400, 100);
  ctx.fillStyle = style.color;
  ctx.font = style.font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name.trim(), 200, 50);
  return sigCanvas.toDataURL('image/png');
};

export const SignatureCanvas = ({ onDrawEnd, canvasRef, height = 150 }) => {
  const padRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const initPad = () => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      if (rect.width === 0) return;

      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = rect.width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = height + 'px';
      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);

      if (padRef.current) {
        padRef.current.off();
      }
      padRef.current = new SignaturePadLib(canvas, {
        penColor: '#1a1a2e',
        backgroundColor: 'rgba(255,255,255,0)',
      });
      padRef.current.addEventListener('endStroke', () => {
        if (onDrawEnd) onDrawEnd();
      });
    };

    const timer = setTimeout(initPad, 100);
    const observer = new ResizeObserver(() => {
      if (containerRef.current && containerRef.current.getBoundingClientRect().width > 0) {
        initPad();
      }
    });
    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      if (padRef.current) {
        padRef.current.off();
        padRef.current = null;
      }
    };
  }, [canvasRef, height, onDrawEnd]);

  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current._pad = padRef;
    }
  });

  return (
    <div ref={containerRef} className="border border-white/20 rounded-sm bg-white overflow-hidden">
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: `${height}px`, touchAction: 'none', display: 'block', cursor: 'crosshair' }}
        data-testid="signature-draw-canvas"
      />
    </div>
  );
};
